import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Modal, Button, Badge, Spinner, ProgressBar } from 'react-bootstrap';

import { postApplicantsImport } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { DroppedFile } from '../hooks/usePageFileDrop';

// Manual applicant file-upload modal. Files are added via addFiles() — either the
// click-to-pick input here, or a page-wide drop (see usePageFileDrop, wired by the
// hosting tab). Uploads start automatically: a self-refilling pool keeps
// CONCURRENCY in flight and queues the rest, so dropping more just extends the
// queue. Cancel aborts everything in flight. Each file's UPLOAD status shows here;
// the classification outcome appears in the File Upload Log.

const ACCEPT = ['.pdf', '.docx', '.txt'];
const ACCEPT_ATTR = ACCEPT.join(',');
const CONCURRENCY = 4;

const accepted = (name: string) => ACCEPT.some((e) => name.toLowerCase().endsWith(e));

type UploadStatus = 'queued' | 'uploading' | 'uploaded' | 'duplicate' | 'error' | 'canceled' | 'skipped';

interface UploadItem {
  id: string;
  file: File;
  path: string;
  status: UploadStatus;
  message?: string;
}

let nextId = 0;
const mkId = () => `u${nextId++}`;

const statusBadge: Record<UploadStatus, { bg: string; text?: string; label: string }> = {
  queued: { bg: 'secondary', label: 'Queued' },
  uploading: { bg: 'info', label: 'Uploading…' },
  uploaded: { bg: 'success', label: 'Uploaded' },
  duplicate: { bg: 'secondary', label: 'Duplicate' },
  error: { bg: 'danger', label: 'Error' },
  canceled: { bg: 'warning', text: 'dark', label: 'Canceled' },
  skipped: { bg: 'light', text: 'dark', label: 'Skipped' },
};

export interface ApplicantImportModalHandle {
  addFiles: (files: DroppedFile[]) => void;
}

interface Props {
  show: boolean;
  onClose: () => void;
  onUploaded?: () => void;
}

const ApplicantImportModal = forwardRef<ApplicantImportModalHandle, Props>(({ show, onClose, onUploaded }, ref) => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // itemsRef is the synchronous source of truth (so the pool can advance the queue
  // without waiting for a React re-render); setItems mirrors it for display.
  const itemsRef = useRef<UploadItem[]>([]);
  const inFlightRef = useRef(0);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const pumpRef = useRef<() => void>();
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  // Auto-scroll the list to the bottom when new files are added (not on status
  // flips), so a fresh drop reveals the latest rows.
  const listRef = useRef<HTMLDivElement>(null);
  const prevLenRef = useRef(0);
  useEffect(() => {
    if (items.length > prevLenRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    prevLenRef.current = items.length;
  }, [items.length]);

  const commit = useCallback((next: UploadItem[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);
  const patch = useCallback((id: string, p: Partial<UploadItem>) => {
    commit(itemsRef.current.map((it) => (it.id === id ? { ...it, ...p } : it)));
  }, [commit]);

  // pump: keep CONCURRENCY uploads in flight, pulling the next queued item as each
  // slot frees. Called on add and on every completion.
  const pump = () => {
    while (inFlightRef.current < CONCURRENCY) {
      const item = itemsRef.current.find((it) => it.status === 'queued');
      if (!item) break;
      patch(item.id, { status: 'uploading' });
      inFlightRef.current++;
      const ctrl = new AbortController();
      controllersRef.current.set(item.id, ctrl);
      postApplicantsImport({ file: item.file }, undefined, ctrl.signal)
        .then((res: any) => patch(item.id, { status: res?.status === 'duplicate' ? 'duplicate' : 'uploaded' }))
        .catch((err: any) => {
          if (ctrl.signal.aborted) patch(item.id, { status: 'canceled' });
          else patch(item.id, { status: 'error', message: err?.response?.data?.message || err?.message || 'upload failed' });
        })
        .finally(() => {
          controllersRef.current.delete(item.id);
          inFlightRef.current--;
          pumpRef.current?.();
          // Pool just went idle with at least one success → let the host refetch
          // its log so the new rows show up live (behind the still-open modal).
          if (
            inFlightRef.current === 0 &&
            !itemsRef.current.some((i) => i.status === 'queued') &&
            itemsRef.current.some((i) => i.status === 'uploaded')
          ) {
            onUploadedRef.current?.();
          }
        });
    }
  };
  pumpRef.current = pump;

  const addFiles = useCallback((files: DroppedFile[]) => {
    if (files.length === 0) return;
    // Skip files already in the list (same name+size+mtime), so dropping a file
    // twice — or a folder overlapping an earlier drop — doesn't re-upload it.
    // error/canceled items are excluded so they can be re-dropped to retry.
    const key = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
    const seen = new Set(
      itemsRef.current.filter((it) => it.status !== 'error' && it.status !== 'canceled').map((it) => key(it.file))
    );
    const newItems: UploadItem[] = [];
    for (const { file, path } of files) {
      const k = key(file);
      if (seen.has(k)) continue;
      seen.add(k);
      newItems.push({
        id: mkId(),
        file,
        path,
        status: accepted(file.name) ? 'queued' : 'skipped',
        message: accepted(file.name) ? undefined : 'unsupported type',
      });
    }
    if (newItems.length === 0) return;
    commit([...itemsRef.current, ...newItems]);
    pumpRef.current?.();
  }, [commit]);

  useImperativeHandle(ref, () => ({ addFiles }), [addFiles]);

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files) addFiles(Array.from(files).map((f) => ({ file: f, path: f.name })));
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [addFiles]
  );

  const cancel = useCallback(() => {
    controllersRef.current.forEach((c) => c.abort());
    commit(itemsRef.current.map((it) => (it.status === 'queued' || it.status === 'uploading' ? { ...it, status: 'canceled' } : it)));
  }, [commit]);

  const active = items.some((i) => i.status === 'uploading' || i.status === 'queued');

  const close = useCallback(() => {
    if (active) return; // must cancel in-flight uploads first
    commit([]);
    onClose();
  }, [active, commit, onClose]);

  const counts = {
    uploaded: items.filter((i) => i.status === 'uploaded').length,
    duplicate: items.filter((i) => i.status === 'duplicate').length,
    error: items.filter((i) => i.status === 'error').length,
    total: items.filter((i) => i.status !== 'skipped').length,
  };
  const done = counts.uploaded + counts.duplicate + counts.error + items.filter((i) => i.status === 'canceled').length;
  const progress = counts.total > 0 ? Math.round((done / counts.total) * 100) : 0;

  return (
    <Modal show={show} onHide={close} size="lg" backdrop={active ? 'static' : true} centered>
      <Modal.Header closeButton={!active}>
        <Modal.Title>Upload applicant files</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          className="border border-2 border-dashed rounded p-4 text-center"
          style={{ cursor: 'pointer' }}
          onClick={() => fileInputRef.current?.click()}
        >
          <i className="bi bi-cloud-arrow-up fs-1 text-primary" />
          <div className="fw-semibold">Drop files or folders anywhere on the page</div>
          <div className="text-muted small">or click here to choose — uploads start automatically ({ACCEPT.join(', ')}; folders recursed)</div>
          <input ref={fileInputRef} type="file" accept={ACCEPT_ATTR} multiple className="d-none" onChange={onPick} />
        </div>

        {items.length > 0 && (
          <>
            {counts.total > 0 && <ProgressBar className="mt-3" now={progress} label={`${done}/${counts.total}`} />}
            <div ref={listRef} className="mt-3" style={{ maxHeight: 320, overflowY: 'auto' }}>
              {items.map((it) => {
                const b = statusBadge[it.status];
                return (
                  <div key={it.id} className="d-flex justify-content-between align-items-center border-bottom py-1 small">
                    <span className="text-truncate me-2" title={it.path}>{it.path}</span>
                    <span className="text-nowrap">
                      {it.message && <span className="text-muted me-2">{it.message}</span>}
                      <Badge bg={b.bg} text={b.text as any}>{b.label}</Badge>
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="text-muted small">
          {counts.total > 0 && <>{counts.total} file{counts.total === 1 ? '' : 's'}{counts.uploaded > 0 && `, ${counts.uploaded} uploaded`}{counts.duplicate > 0 && `, ${counts.duplicate} duplicate`}{counts.error > 0 && `, ${counts.error} failed`}</>}
        </div>
        <div className="d-flex gap-2 align-items-center">
          {active ? (
            <>
              <Spinner animation="border" size="sm" />
              <Button variant="outline-danger" onClick={cancel}>
                <i className="bi bi-x-circle me-1" />Cancel
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={close}>Close</Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
});

ApplicantImportModal.displayName = 'ApplicantImportModal';

export default ApplicantImportModal;
